import React, { useEffect, useMemo, useState } from 'react';
import { OrganizationInput } from '../Subscriptions/components';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import { useAbstractAWSAccountsForm } from './AbstractAWSAccountsForm';
import { useNavigate, useParams } from 'react-router-dom';
import EnvironmentInput from './components/EnvironmentInput';
import Organization from '../../models/entities/Organization';
import { getOrganizations } from '../common/OrganizationStorage';
import CartaInput from '../common/form-controls/Lookups/CartaInput';
import { Grid, Alert, TextInput, ToggleField, Button, Textarea, Select } from '../common';
import UserLookup from '../common/form-controls/Lookups/UserLookup';
import WorkflowDetailWrapper from '../common/wrappers/WorkflowDetailWrapper';
import AWSAccountsService from '../../services/AWSAccountsService';
import AWSAccountRequest from '../../models/entities/AWSAccountRequest';
import UserLookupObject from '../../models/UserLookupObject';
import GroupsService from '../../services/GroupsService';
import ConfirmModal from '../common/modals/ConfirmModal';
import AccountConnectivitySettingsTable from './components/connectivitySettings/AccountConnectivitySettingsTable';
import { ButtonBar } from '../common/components/ButtonBar';
import { SaveDiskIcon, XFilledIcon } from 'react-unity';
import { RequestAWSAccountVPCModel } from '../../models/viewModels/AWSAccounts/RequestAWSAccountVPCModel';
import AWSAccountConnectivityConfiguration from '../../models/entities/AWSAccountConnectivityConfiguration';
import { Validation } from '../../utils/Validation';
import { WorkflowState } from '../../models/enums/WorkflowState';
import { Checkbox, CheckboxGroup } from '@emorg-prd/standard-react';
import './AWSAccountsCreate.css';
import MultipleSelectField from '../common/form-controls/MultipleSelectField';
import { authenticatedUser } from '../../authentication/authModule';
import { AWSEnvironment } from '../../models/enums/AWSEnvironment';
import CartaId from '../../models/entities/CartaId';
import EnvironmentConfiguration from '../../utils/EnvironmentConfiguration';
import StubGroupsService from '../../services/StubGroupService';
import AccountGroupsEditor from './components/AccountGroupsEditor';
import Group from '../../models/Group';
import { PermissionSetsResponse } from '../../models/viewModels/AWSAccounts/PermissionSetsResponse';
import { isRequiredPermissionSet, getRequiredPermissionSets, getOtherPermissionSets } from '../../models/entities/AWSAccountGroupTypes';
import TransferAwsAccountModal from './components/TransferAwsAccountModal';
import GroupLookup from '../common/form-controls/Lookups/GroupLookup';
import ExtendPocModal from './components/ExtendPocModal';
import RenewalDecisionModal from './components/RenewalDecisionModal';

interface DecisionModalState {
    approve: boolean;
    complete: boolean;
    reject: boolean;
    cancel: boolean;
    delete: boolean;
}

const getFormattedTitle = (account: AWSAccountRequest | null) => {
    var name = account?.accountName;
    if (account?.awsAccountNumber) {
        name = `${name} (${account.awsAccountNumber})`;
    }
    return name || '';
}


const AWSAccountsDetail = () => {
    const {
        state,
        formModel,
        setState,
        getStateFormHandlers,
        handleEndModal,
        setSubmissionAlertError,
        handleStateFormChange,
        userNotAllowed,
        getLinkTree,
        isValid,
        endAndRedirect,
    } = useAbstractAWSAccountsForm();
    const params = useParams();
    const awsAccountService = new AWSAccountsService();
    const [account, setAccount] = useState<AWSAccountRequest>();
    const [loading, setLoading] = useState<boolean>(true);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [baselineEditableCompare, setBaselineEditableCompare] = useState<any>(null);
    const [baselineEditableRestore, setBaselineEditableRestore] = useState<any>(null);
    const stateFormHandler = () => getStateFormHandlers();
    const ownerInTransferField = stateFormHandler().ownerInTransfer;
    const [organizations, setOrganizations] = useState<Organization[]>();
    const [submissionAlert, setSubmissionAlert] = useState<AlertBanner>({ visible: false });
    const [validationErrors, setValidationErrors] = useState<Array<{ field: string; message: string }>>([]);
    const [submissionInProgress, setSubmissionInProgress] = useState<boolean>(false);
    const [modalComment, setModalComment] = useState<string>('');
    const [decisionModalVisible, setDecisionModalVisible] = useState<DecisionModalState>({
        approve: false,
        complete: false,
        reject: false,
        cancel: false,
        delete: false,
    });
    const [editNetConfiguration, setEditNetworkConfiguration] = useState<boolean>(false);
    const [editConnectivityMode, setEditConnectivityMode] = useState<boolean>(false);
    const [baselineConnectivitySettings, setBaselineConnectivitySettings] = useState<AWSAccountConnectivityConfiguration[] | null>(null);
    const [termsChecked, setTermsChecked] = useState<boolean>(false);
    const [transferModalVisible, setTransferModalVisible] = useState<boolean>(false);
    const [cancelTransferModalVisible, setCancelTransferModalVisible] = useState<boolean>(false);
    const [extendPocModalVisible, setExtendPocModalVisible] = useState<boolean>(false);
    const [isTier0, setIsTier0] = useState<boolean>(false);
    const [isCyberCritical, setIsCyberCritical] = useState<boolean>(false);
    const navigate = useNavigate();

    const groupsService = useMemo(
        () => (EnvironmentConfiguration.isDev() ? new StubGroupsService() : new GroupsService()),
        []
    );

    const [renewalDecisionModal, setRenewalDecisionModal] = useState<{
        visible: boolean;
        decision: 'approve' | 'delete';
    }>({ visible: false, decision: 'approve' });
    const [permissionSets, setPermissionSets] = useState<PermissionSetsResponse | null>(null);
    const [requiredGroupAssignments, setRequiredGroupAssignments] = useState<Record<string, Group | null>>({});
    const [featureFlags, setFeatureFlags] = useState<{
        enableNotImportedAccountModifications: boolean;
        enableImportedAccountModifications: boolean;
        enableNotImportedAccountGroupModifications: boolean;
        enableImportedAccountGroupModifications: boolean;
        enableNotImportedAccountTransfers: boolean;
        enableImportedAccountTransfers: boolean;
        enableAwsAccountDeletion : boolean;
        enableAwsPOCAccountRenewal: boolean;
        enableAwsTier0Import: boolean;
        enableAwsTier0Creation: boolean;
        enableAwsCyberCriticalCreation : boolean;
    } | null>(null);

    const getAdditionalAccountGroups = (
        accountGroups: Record<string, string[]>,
        requiredAssignments: Record<string, Group | null>
    ): Record<string, string[]> => {
        if (!permissionSets) return accountGroups || {};

        const requiredPermissionSetNames = getRequiredPermissionSets(permissionSets);
        const result: Record<string, string[]> = {};

        for (const [permissionSet, groupIds] of Object.entries(accountGroups || {})) {
            if (!Array.isArray(groupIds) || groupIds.length === 0) continue;

            if (requiredPermissionSetNames.includes(permissionSet)) {
                const requiredGroupId = requiredAssignments[permissionSet]?.id;
                const filteredGroupIds = requiredGroupId 
                    ? groupIds.filter((id) => id !== requiredGroupId)
                    : [];
                
                if (filteredGroupIds.length > 0) {
                    result[permissionSet] = filteredGroupIds;
                }
            } else {
                result[permissionSet] = groupIds;
            }
        }
        return result;
    };

    const buildFullAccountGroups = (
        requiredAssignments: Record<string, Group | null>,
        additionalGroups: Record<string, string[]>
    ): Record<string, string[]> => {
        const result: Record<string, string[]> = {};

        for (const [permissionSet, groupIds] of Object.entries(additionalGroups)) {
            result[permissionSet] = [...groupIds];
        }

        for (const [permissionSet, group] of Object.entries(requiredAssignments)) {
            if (group) {
                if (!result[permissionSet]) {
                    result[permissionSet] = [];
                }
                result[permissionSet] = [
                    group.id,
                    ...result[permissionSet].filter((id) => id !== group.id),
                ];
            }
        }

        return result;
    };

    const handleRequiredGroupChange = (permissionSet: string, group: Group | null) => {
        const newRequiredAssignments = {
            ...requiredGroupAssignments,
            [permissionSet]: group,
        };
        setRequiredGroupAssignments(newRequiredAssignments);

        const additionalGroups = getAdditionalAccountGroups(
            (state.stateForm as any).accountGroups || {},
            newRequiredAssignments
        );
        const fullAccountGroups = buildFullAccountGroups(newRequiredAssignments, additionalGroups);
        
        setState((prev) => ({
            ...prev,
            stateForm: {
                ...prev.stateForm,
                accountGroups: fullAccountGroups,
            },
        }));
    };

    const handleAdditionalGroupsChange = (additionalGroups: Record<string, string[]>) => {
        const fullAccountGroups = buildFullAccountGroups(requiredGroupAssignments, additionalGroups);
        setState((prev) => ({
            ...prev,
            stateForm: {
                ...prev.stateForm,
                accountGroups: fullAccountGroups,
            },
        }));
    };

    const canEditOrganization = (): boolean => {
        // ONLY IMPORTED ACCOUNTS EDITABLE
        //if (!account?.isImportedAccount()) return false;
        //return !account?.organization || account?.organization?.id === undefined || account?.organization?.id <= 0;
        return true;
    };

    const canEditBusinessJustification = (): boolean => {
        if (!account?.isImportedAccount()) return false;
        return !account?.accountUsedFor || account?.accountUsedFor.trim() === '';
    };

    const EDITABLE_KEYS = [
        'appName',
        'internalOrder',
        'cartaIds',
        'accountGroups',
        'custodian',
        'selectedL3',
        'selectedL4',
        'selectedOrganization',
        'accountUsedFor',
        'isTier0',
        'isCyberCritical',
    ] as const;

    const pickEditable = (form: any) => {
        const out: any = {};
        EDITABLE_KEYS.forEach((k) => (out[k] = form?.[k]));
        return out;
    };

    const buildEditableCompare = (formLike: any) => {
        const picked = pickEditable(formLike);
        const cmp: any = {};
        cmp.appName = picked.appName ?? '';
        cmp.internalOrder = picked.internalOrder ?? '';
        cmp.custodianId = picked.custodian?.id ?? null;
        cmp.selectedL3 = picked.selectedL3 ?? null;
        cmp.selectedL4 = picked.selectedL4 ?? null;
        cmp.selectedOrganization = picked.selectedOrganization ?? null;
        cmp.accountUsedFor = picked.accountUsedFor ?? '';
        cmp.isTier0 = picked.isTier0 ?? false;
        cmp.isCyberCritical = picked.isCyberCritical ?? false;

        cmp.accountGroups = {};
        if (picked.accountGroups && typeof picked.accountGroups === 'object') {
            Object.keys(picked.accountGroups)
                .sort()
                .forEach((permissionSet) => {
                    const groups = picked.accountGroups[permissionSet];
                    if (Array.isArray(groups) && groups.length > 0) {
                        cmp.accountGroups[permissionSet] = groups
                            .map((g) => (typeof g === 'string' ? g : String(g)))
                            .sort();
                    }
                });
        }

        cmp.cartaIds = Array.isArray(picked.cartaIds) ? picked.cartaIds.map((c) => c.cartaAppId).sort() : [];

        return cmp;
    };

    const buildEditableRestore = (form) => {
        const picked = pickEditable(form);

        const accountGroupsCopy: Record<string, string[]> = {};
        if (picked.accountGroups && typeof picked.accountGroups === 'object') {
            Object.keys(picked.accountGroups).forEach((permissionSet) => {
                const groups = picked.accountGroups[permissionSet];
                if (Array.isArray(groups)) {
                    accountGroupsCopy[permissionSet] = groups.slice();
                }
            });
        }

        return {
            appName: picked.appName ?? '',
            internalOrder: picked.internalOrder ?? '',
            cartaIds: Array.isArray(picked.cartaIds) ? picked.cartaIds.slice() : [],
            accountGroups: accountGroupsCopy,
            custodian: picked.custodian
                ? picked.custodian instanceof UserLookupObject
                    ? picked.custodian
                    : UserLookupObject.fromUser(picked.custodian)
                : null,
            selectedL3: picked.selectedL3 ?? null,
            selectedL4: picked.selectedL4 ?? null,
            selectedOrganization: picked.selectedOrganization ?? null,
            accountUsedFor: picked.accountUsedFor ?? '',
            isTier0: picked.isTier0 ?? false,
            isCyberCritical: picked.isCyberCritical ?? false,
        };
    };

    const isDirtyEditable = (() => {
        if (!baselineEditableCompare) return false;
        const now = buildEditableCompare(state?.stateForm || {});
        return JSON.stringify(now) !== JSON.stringify(baselineEditableCompare);
    })();

    const isEqual = (a: any, b: any) => {
        return JSON.stringify(a) === JSON.stringify(b);
    };

    type EditableCompare = ReturnType<typeof buildEditableCompare>;

    function buildPatchPayloadFromDiff(
        prev: EditableCompare,
        curr: EditableCompare
    ): {
        custodianId?: string;
        accountGroups?: Record<string, string[]>;
        cartaIds?: CartaId[];
        appName?: string;
        internalOrder?: string;
        organizationId?: number;
        accountUsedFor?: string;
    } {
        const patch: any = {};

        if (!isEqual(prev.custodianId, curr.custodianId)) {
            patch.custodianId = curr.custodianId ?? undefined;
        }

        const hasAccountGroupsChanged = !isEqual(prev.accountGroups, curr.accountGroups);

        if (hasAccountGroupsChanged) {
            const accountGroupsPayload: Record<string, string[]> = {};

            Object.keys(curr.accountGroups || {}).forEach((permissionSet) => {
                const groupIds = curr.accountGroups[permissionSet];
                if (Array.isArray(groupIds) && groupIds.length > 0) {
                    accountGroupsPayload[permissionSet] = groupIds;
                }
            });

            if (Object.keys(accountGroupsPayload).length > 0) {
                patch.accountGroups = accountGroupsPayload;
            }
        }

        if (!isEqual(prev.cartaIds, curr.cartaIds)) {
            patch.cartaIds = undefined;
        }

        if (!isEqual(prev.appName, curr.appName)) {
            patch.appName = curr.appName ?? undefined;
        }
        
        if (!isEqual(prev.internalOrder, curr.internalOrder)) {
            patch.internalOrder = curr.internalOrder ?? '';
        }
        
        if (!isEqual(prev.selectedOrganization, curr.selectedOrganization)) {
            const parsedOrgId = curr.selectedOrganization ? parseInt(curr.selectedOrganization) : undefined;
            if (parsedOrgId && !isNaN(parsedOrgId)) {
                patch.organizationId = parsedOrgId;
            }
        }
        
        if (!isEqual(prev.accountUsedFor, curr.accountUsedFor)) {
            patch.accountUsedFor = curr.accountUsedFor ?? undefined;
        }

        if (!isEqual(prev.isTier0, curr.isTier0)) {
            patch.isTier0 = curr.isTier0;
        }

        if (!isEqual(prev.isCyberCritical, curr.isCyberCritical)) {
            patch.isCyberCritical = curr.isCyberCritical;
        }
        
        return patch;
    }

    const handleAlertClose = () => {
        setSubmissionAlert({ visible: false });
        setValidationErrors([]);
    };

    const extractErrorMessage = (err: any, fallback: string): string => {
        const data = err?.response?.data;
        if (!data) return err?.message ?? fallback;
        if (typeof data === 'string') return data;
        return data.message ?? data.title ?? fallback;
    };

    const fetchFeatureFlags = async () => {
        try {
            const flags = await awsAccountService.getFeatureFlags();
            setFeatureFlags(flags);
        } catch (error) {
            console.error('Failed to fetch feature flags:', error);
        }
    };

    const fetchPermissionSets = async (isManaged: boolean) => {
        try {
            const sets = await awsAccountService.getPermissionSets(isManaged);
            setPermissionSets(sets);
            return sets;
        } catch (error) {
            console.error('Failed to fetch permission sets:', error);
            setSubmissionAlert({
                visible: true,
                variant: 'error',
                text: 'Failed to load permission sets. Please refresh the page.',
            });
            return null;
        }
    };

    const initializeRequiredGroupAssignments = async (
        accountGroups: Record<string, string[]>,
        permSets: PermissionSetsResponse
    ) => {
        const requiredSets = getRequiredPermissionSets(permSets);
        const assignments: Record<string, Group | null> = {};

        for (const permissionSet of requiredSets) {
            const groupIds = accountGroups[permissionSet];
            if (groupIds && groupIds.length > 0) {
                try {
                    const group = await groupsService.getById(groupIds[0]);
                    assignments[permissionSet] = group;
                } catch (error) {
                    console.error(`Failed to fetch group for ${permissionSet}:`, error);
                    assignments[permissionSet] = null;
                }
            } else {
                assignments[permissionSet] = null;
            }
        }

        setRequiredGroupAssignments(assignments);
    };

    const fetchAccount = async () => {
        setLoading(true);
        try {
            const id = parseInt(params.id);
            const request = await awsAccountService.getById(id);
            const mappedRequest = new AWSAccountRequest(request);
            const organizations = (await getOrganizations()).map((o) => new Organization(o));
            mappedRequest.organization?.setLevels(organizations);

            const permSets = await fetchPermissionSets(mappedRequest.isManaged);

            if (permSets && mappedRequest.accountGroups) {
                await initializeRequiredGroupAssignments(mappedRequest.accountGroups, permSets);
            }

            setAccount(mappedRequest);
            setIsTier0(mappedRequest.isTier0 ?? false);
            setIsCyberCritical(mappedRequest.isCyberCritical ?? false);
            setState((prev) => ({
                ...prev,
                stateForm: {
                    ...prev.stateForm,
                    ...mappedRequest,
                    selectedL3: mappedRequest.organization?.L3.id.toString(),
                    selectedL4: mappedRequest.organization?.L4.id.toString(),
                    selectedOrganization: mappedRequest.organization?.id.toString(),
                    owner: UserLookupObject.fromUser(request?.owner),
                    custodian: UserLookupObject.fromUser(request?.custodian),
                    accountGroups: mappedRequest.accountGroups || {},
                    networkConfigList: mappedRequest.network,
                    environment: mappedRequest.environment,
                    pocDurationDays: mappedRequest.pocDurationDays?.toString(),
                },
            }));
            formModel.fields.networkConfig.getValue = () =>
                mappedRequest.network.map((net) => new AWSAccountConnectivityConfiguration(net));
            formModel.fields.networkConfig.validation = new Validation({
                required: () =>
                    request?.workflowInstance.currentWorkflowInstanceState.workflowState.value ==
                    WorkflowState.PendingAwsConnectivityDetails.value && request.isManaged,
            });
            const initialForRestore = {
                appName: mappedRequest.appName,
                internalOrder: mappedRequest.internalOrder,
                cartaIds: mappedRequest.cartaIds,
                accountGroups: mappedRequest.accountGroups || {},
                custodian: UserLookupObject.fromUser(request?.custodian),
                selectedL3: mappedRequest.organization?.L3?.id?.toString() || '',
                selectedL4: mappedRequest.organization?.L4?.id?.toString() || '',
                selectedOrganization: mappedRequest.organization?.id?.toString() || '',
            };
            setBaselineEditableRestore(buildEditableRestore(initialForRestore));
            setBaselineEditableCompare(buildEditableCompare(initialForRestore));
        } catch (ex) {
            setSubmissionAlertError(ex.response?.data?.message);
        }
        setLoading(false);
    };

    const enterEditMode = () => {
        const editableNow = pickEditable(state.stateForm);
        setBaselineEditableRestore(buildEditableRestore(editableNow));
        setBaselineEditableCompare(buildEditableCompare(editableNow));

        setState((prev) => ({
            ...prev,
            stateForm: {
                ...prev.stateForm,
                appName: prev.stateForm.appName || '',
            },
        }));

        setEditMode(true);
    };

    const cancelEdit = () => {
        if (!baselineEditableRestore) return setEditMode(false);
        setState((prev) => ({
            ...prev,
            stateForm: {
                ...prev.stateForm,
                ...baselineEditableRestore,
            },
        }));
        setIsTier0(baselineEditableRestore.isTier0 ?? false);
        setIsCyberCritical(baselineEditableRestore.isCyberCritical ?? false);
        
        if (permissionSets && baselineEditableRestore.accountGroups) {
            initializeRequiredGroupAssignments(baselineEditableRestore.accountGroups, permissionSets);
        }
        
        setEditMode(false);
    };

    const saveEdit = async () => {
        if (!isDirtyEditable) {
            setEditMode(false);
            return;
        }

        setSaving(true);
        setSubmissionAlert({ visible: true, text: 'Saving changes...', variant: 'default' });

        try {
            const prevCompare = baselineEditableCompare as ReturnType<typeof buildEditableCompare>;
            const currCompare = buildEditableCompare(state.stateForm || {});
            const diffPayload = buildPatchPayloadFromDiff(prevCompare, currCompare);

            if (!isEqual(prevCompare.cartaIds, currCompare.cartaIds)) {
                const formCarta = state.stateForm.cartaIds;
                diffPayload.cartaIds = Array.isArray(formCarta) ? formCarta.map((c: any) => ({ ...c })) : undefined;
            }
            const payload = Object.fromEntries(
                Object.entries(diffPayload).filter(([_, v]) => v !== undefined)
            ) as typeof diffPayload;
            if (Object.keys(payload).length === 0) {
                setSubmissionAlert({ visible: true, text: 'No changes to save.', variant: 'default' });
                setEditMode(false);
                setSaving(false);
                return;
            }
            await awsAccountService.updateAccountRequest(payload, account.id);
            setSubmissionAlert({ visible: true, text: 'Changes saved successfully.', variant: 'success' });
            const freshRestore = buildEditableRestore(state.stateForm);
            const freshCompare = buildEditableCompare(state.stateForm);
            setBaselineEditableRestore(freshRestore);
            setBaselineEditableCompare(freshCompare);
            setEditMode(false);
            setSaving(false);
            
            setTimeout(() => {
                navigate('/awsAccounts');
            }, 2000);
            return;
        } catch (err: any) {
            const responseData = err?.response?.data;
            const backendErrors = responseData?.errors as Record<string, string[]> | undefined;
            const globalMessage = typeof responseData === 'string' 
                ? responseData 
                : responseData?.title;
            let errorText = globalMessage || 'An error occurred while saving. Please try again.';
            if (backendErrors) {
                const details = Object.entries(backendErrors)
                    .flatMap(([field, messages]) => messages.map((msg) => `${field}: ${msg}`))
                    .join('\n');
                errorText = `${errorText}\n${details}`;
            }
            setSubmissionAlert({
                visible: true,
                variant: 'error',
                text: errorText,
            });
        } finally {
            setSaving(false);
        }
    };

    const openTransferModal = () => {
        ownerInTransferField.onChange(null);
        setTransferModalVisible(true);
    };

    const closeTransferModal = () => {
        ownerInTransferField.onChange(null);
        setTransferModalVisible(false);
    };

    const isInTransfer =
        account?.workflowInstance?.currentWorkflowInstanceState?.workflowState?.value ===
        WorkflowState.PendingNewAccountOwnerApproval.value;

    const handleTransferOwnership = async () => {
        const newOwner = stateFormHandler().ownerInTransfer.value;
        const newCustodian = stateFormHandler().custodianInTransfer.value;
        const currentCustodian = account?.custodian;
        if (!newOwner) return;
        if (currentCustodian && newOwner.id === currentCustodian.id && !newCustodian) {
            return;
        }

        setSubmissionInProgress(true);
        setSubmissionAlert({
            visible: true,
            text: 'Submitting transfer...',
            variant: 'default',
        });

        try {
            const body: {
                newOwnerId: string;
                newCustodianId?: string;
            } = {
                newOwnerId: newOwner.id,
            };

            if (newCustodian) {
                body.newCustodianId = newCustodian.id;
            }
            closeTransferModal();
            await awsAccountService.transferOwnership(body, account.id);
            setSubmissionAlert({
                visible: true,
                text: 'Ownership transfer requested successfully.',
                variant: 'success',
            });

            setTimeout(() => {
                navigate('/awsAccounts');
            }, 5000);

        } catch (err: any) {
            setSubmissionAlert({
                visible: true,
                variant: 'error',
                text:
                    err?.response?.data || err?.message || 'Unable to submit transfer at this time. Please try again.',
            });
        } finally {
            setSubmissionInProgress(false);
        }
    };

    const openCancelTransferModal = () => setCancelTransferModalVisible(true);
    const closeCancelTransferModal = () => setCancelTransferModalVisible(false);

    const handleCancelTransferOwnership = async () => {
        closeCancelTransferModal();
        setSubmissionInProgress(true);
        setSubmissionAlert({ visible: true, text: 'Canceling transfer...', variant: 'default' });
        try {
            await awsAccountService.cancelAccountRequest('Trasfer Ownership was cancelled by user', account.id);

            setSubmissionAlert({
                visible: true,
                text: 'Ownership transfer canceled successfully.',
                variant: 'success',
            });
        } catch (err: any) {
            setSubmissionAlert({
                visible: true,
                variant: 'error',
                text:
                    err?.response?.data?.message ||
                    err?.message ||
                    'Unable to cancel the transfer at this time. Please try again.',
            });
        } finally {
            setSubmissionInProgress(false);
        }
    };

    const handleApprove = async () => {
        closeDecisionModal('approve');
        setSubmissionInProgress(true);
        try {
            await awsAccountService.approveAccountRequest(modalComment, account.id);
            setSubmissionAlert({
                visible: true,
                text: 'Account request approved successfully.',
                variant: 'success',
            });
            handleRedirect();
        } catch (ex) {
            setSubmissionAlertError(ex.response?.data?.message);
        }
        setSubmissionInProgress(false);
    };

    const handleComplete = async () => {
        closeDecisionModal('complete');
        setSubmissionInProgress(true);
        try {
            await awsAccountService.completeAccountRequest(modalComment, account.id);
            setSubmissionAlert({
                visible: true,
                text: 'Account request completed successfully.',
                variant: 'success',
            });
            handleRedirect();
        } catch (ex) {
            setSubmissionAlertError(ex.response?.data?.message);
        }
        setSubmissionInProgress(false);
    };

    const handleReject = async () => {
        closeDecisionModal('reject');
        setSubmissionInProgress(true);
        try {
            await awsAccountService.rejectAccountRequest(modalComment, account.id);
            setSubmissionAlert({
                visible: true,
                text: 'Account request rejected successfully.',
                variant: 'success',
            });
            handleRedirect();
        } catch (ex) {
            setSubmissionAlertError(ex.response?.data?.message);
        }
        setSubmissionInProgress(false);
    };

    const handleCancellation = async () => {
        closeDecisionModal('cancel');
        setSubmissionInProgress(true);
        try {
            await awsAccountService.cancelAccountRequest(modalComment, account.id);
            setSubmissionAlert({
                visible: true,
                text: 'Account request cancelled successfully.',
                variant: 'success',
            });
            handleRedirect();
        } catch (ex) {
            setSubmissionAlertError(ex.response?.data?.message);
        }
        setSubmissionInProgress(false);
    };

    const handleDeletion = async () => {
        closeDecisionModal('delete');
        setSubmissionInProgress(true);
        try {
            await awsAccountService.deleteAccountRequest(modalComment, account.id);
            setSubmissionAlert({
                visible: true,
                text: 'Account delete request submitted successfully.',
                variant: 'success',
            });
            handleRedirect();
        } catch (ex) {
            setSubmissionAlertError(ex.response?.data?.message);
        }
        setSubmissionInProgress(false);
    };

    const handleApproveRenewal = async () => {
        setRenewalDecisionModal({ visible: false, decision: 'approve' });
        setSubmissionInProgress(true);
        setSubmissionAlert({ visible: true, text: 'Approving PoC extension...', variant: 'default' });
        try {
            await awsAccountService.extendPoc(
                account!.id,
                account!.pocDurationDays,
                'PoC extension approved by architect'
            );
            setSubmissionAlert({
                visible: true,
                text: 'PoC extension approved. The account is active again.',
                variant: 'success',
            });
            handleRedirect();
        } catch (err: any) {
            setSubmissionAlert({
                visible: true,
                variant: 'error',
                text: extractErrorMessage(err, 'Failed to extend PoC.'),
            });
        }
        setSubmissionInProgress(false);
    };

    const handleDeleteFromRenewal = async () => {
        setRenewalDecisionModal({ visible: false, decision: 'delete' });
        setSubmissionInProgress(true);
        setSubmissionAlert({ visible: true, text: 'Deleting account...', variant: 'default' });
        try {
            await awsAccountService.deleteAccountRequest(
                'PoC extension denied by architect. Account deleted.',
                account.id
            );
            setSubmissionAlert({
                visible: true,
                text: 'Account deletion requested successfully.',
                variant: 'success',
            });
            handleRedirect();
        } catch (ex: any) {
            setSubmissionAlertError(ex.response?.data?.message);
        }
        setSubmissionInProgress(false);
    };

    const handleRedirect = () => {
        setTimeout(() => {
            navigate('/awsAccounts');
        }, 5000);
    };

    const openDecisionModal = (type: 'approve' | 'reject' | 'complete' | 'cancel'| 'delete') => {
        setDecisionModalVisible((prevState) => ({
            ...prevState,
            [type]: true,
        }));
    };

    const closeDecisionModal = (type: 'approve' | 'reject' | 'complete' | 'cancel' | 'delete') => {
        setDecisionModalVisible((prevState) => ({
            ...prevState,
            [type]: false,
        }));
    };

    const decisionModalConfirmationField = () => ({
        label: 'Please, provide the reason.',
        value: modalComment,
        validation: {
            required: true,
        },
        onChange: (ev) => {
            setModalComment(ev.target.value);
        },
    });

    useEffect(() => {
        fetchFeatureFlags();
        fetchAccount();
    }, []);

    const canEditGroups = (): boolean => {
        if (!editMode) return false;
        if (!featureFlags) return false;
        
        if (account?.isImportedAccount()) {
            return featureFlags.enableImportedAccountGroupModifications === true;
        }
        return featureFlags.enableNotImportedAccountGroupModifications === true;
    };

    // NEED TO DEFINE WHAT WE DO WITH TIER 0, IF WE SEARCH THE GROUPS WE KEEP THIS AND ADD THE SEARCH FOR THE GROUPS ON THE TENANT
    const renderRequiredGroupInputs = () => {
        if (!permissionSets) return null;
        
        const requiredSets = getRequiredPermissionSets(permissionSets);
        if (requiredSets.length === 0) return null;

        const groupsEditable = canEditGroups();

        return (
            <>
                <h4>Required Role Groups</h4>
                <Grid>
                    {requiredSets.map((permissionSet) => (
                        <Grid.Item key={permissionSet}>
                            <GroupLookup
                                label={`${permissionSet} *`}
                                disabled={!groupsEditable}
                                placeholder="Search for a group..."
                                note={`Azure AD group for ${permissionSet} permission set`}
                                value={requiredGroupAssignments[permissionSet]?.displayName || ''}
                                onChange={(group) => handleRequiredGroupChange(permissionSet, group)}
                                groupsService={groupsService}
                            />
                        </Grid.Item>
                    ))}
                </Grid>
                <br />
            </>
        );
    };

    /*const renderRequiredGroupInputs = () => {
        if (!permissionSets) return null;

        const roleSets = isTier0
            ? getOtherPermissionSets(permissionSets)
            : getRequiredPermissionSets(permissionSets);

        if (roleSets.length === 0) return null;

        const groupsEditable = canEditGroups();

        return (
            <>
                <h4>{isTier0 ? 'Tier 0 Role Groups' : 'Required Role Groups'}</h4>
                <Grid>
                    {roleSets.map((permissionSet) => (
                        <Grid.Item key={permissionSet}>
                            {isTier0 ? (
                                <TextInput
                                    label={`${permissionSet} *`}
                                    disabled={!groupsEditable}
                                    placeholder="Enter the group name..."
                                    note={`Group name for ${permissionSet} permission set`}
                                    value={requiredGroupAssignments[permissionSet]?.displayName || ''}
                                    onChange={(ev) =>
                                        handleRequiredGroupTextChange(permissionSet, ev.target.value)
                                    }
                                />
                            ) : (
                                <GroupLookup
                                    label={`${permissionSet} *`}
                                    disabled={!groupsEditable}
                                    placeholder="Search for a group..."
                                    note={`Azure AD group for ${permissionSet} permission set`}
                                    value={requiredGroupAssignments[permissionSet]?.displayName || ''}
                                    onChange={(group) => handleRequiredGroupChange(permissionSet, group)}
                                    groupsService={groupsService}
                                />
                            )}
                        </Grid.Item>
                    ))}
                </Grid>
                <br />
            </>
        );
    };*/

    const handleTier0Change = (checked: boolean) => {
        setIsTier0(checked);
        setState((prev) => ({
            ...prev,
            stateForm: { ...prev.stateForm, isTier0: checked },
        }));
    };

    const canEditTier0 = (): boolean => editMode && featureFlags?.enableAwsTier0Creation === true;

    const handleCyberCriticalChange = (checked: boolean) => {
        setIsCyberCritical(checked);
        setState((prev) => ({
            ...prev,
            stateForm: { ...prev.stateForm, isCyberCritical: checked },
        }));
    };

    const canEditCyberCritical = (): boolean => editMode && featureFlags?.enableAwsCyberCriticalCreation === true;

    const handleRequiredGroupTextChange = (permissionSet: string, rawValue: string) => {
        const value = rawValue?.trim();
        const pseudoGroup = value ? ({ id: value, displayName: value } as Group) : null;
        handleRequiredGroupChange(permissionSet, pseudoGroup);
    };

    const Actions = () => {
        const scrollToConnectivitySettings = () => {
            const element =
                document.querySelector('[data-testid="connectivity-settings"]') ||
                document.querySelector('table') ||
                document.querySelector('.connectivity-settings');

            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest',
                });
            }
        };

        const handleEditClick = () => {
            setEditNetworkConfiguration(true);
            setTimeout(scrollToConnectivitySettings, 100);
        };

        return (
            <ButtonBar>
                {account?.isVpcEditable() && !account?.isImportedAccount() && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Edit"
                            size="small"
                            onClick={handleEditClick}
                            disabled={loading || editNetConfiguration || submissionInProgress}
                            isLoading={submissionInProgress}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}

                {account?.isEditable() && 
                    !editMode && 
                    featureFlags && 
                    (account?.isImportedAccount() 
                        ? featureFlags.enableImportedAccountModifications 
                        : featureFlags.enableNotImportedAccountModifications) && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Edit Account"
                            size="small"
                            onClick={enterEditMode}
                            disabled={loading || submissionInProgress || saving || editConnectivityMode}
                            isLoading={loading}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}

                {account?.canEditConnectivitySettings() && !editConnectivityMode && !editMode && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Edit Connectivity Settings"
                            size="small"
                            onClick={() => {
                                enterConnectivityEditMode();
                                setTimeout(scrollToConnectivitySettings, 100);
                            }}
                            disabled={loading || submissionInProgress || saving}
                            isLoading={loading}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}

                {editConnectivityMode && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Save Connectivity"
                            variant="primary"
                            size="small"
                            onClick={handleSaveConnectivitySettings}
                            disabled={loading || submissionInProgress || state.stateForm.networkConfigList?.length === 0}
                            isLoading={submissionInProgress}
                        />
                        &nbsp;
                        <Button
                            name="Cancel"
                            variant="secondary"
                            size="small"
                            onClick={cancelConnectivityEdit}
                            disabled={loading || submissionInProgress}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}

                {editMode && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Save"
                            variant="primary"
                            size="small"
                            onClick={saveEdit}
                            disabled={loading || saving || !isDirtyEditable}
                            isLoading={saving}
                        />
                        &nbsp;
                        <Button
                            name="Cancel"
                            variant="secondary"
                            size="small"
                            onClick={cancelEdit}
                            disabled={loading || saving}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}

                {account?.isEditable?.() && 
                    !isInTransfer && 
                    featureFlags && 
                    (account?.isImportedAccount() 
                        ? featureFlags.enableImportedAccountTransfers 
                        : featureFlags.enableNotImportedAccountTransfers) && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Change Account Owner"
                            size="small"
                            onClick={openTransferModal}
                            disabled={loading || submissionInProgress || saving}
                            isLoading={submissionInProgress}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}

                {isInTransfer && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Cancel Owner Change"
                            size="small"
                            onClick={openCancelTransferModal}
                            disabled={loading || submissionInProgress || saving}
                            isLoading={submissionInProgress}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}

                {account?.canHaveOwnershipTransferDecision() && (
                    <>
                        <ButtonBar.Item separator>
                            <Button
                                name="Approve Ownership Transfer"
                                size="small"
                                isLoading={submissionInProgress}
                                disabled={submissionInProgress}
                                onClick={() => {
                                    openDecisionModal('approve');
                                }}
                            />
                            <br />
                            <br />
                        </ButtonBar.Item>
                        <ButtonBar.Item separator>
                            <Button
                                name="Reject Ownership Transfer"
                                size="small"
                                isLoading={submissionInProgress}
                                disabled={submissionInProgress}
                                onClick={() => {
                                    openDecisionModal('reject');
                                }}
                            />
                            <br />
                            <br />
                        </ButtonBar.Item>
                    </>
                )}

                {(account?.canHaveTechnicalDecision() || account?.canHaveAccountOwnerDecision()) && (
                    <>
                        <ButtonBar.Item separator>
                            <Button
                                name="Approve"
                                size="small"
                                isLoading={submissionInProgress}
                                disabled={
                                    (account?.workflowInstance.currentWorkflowInstanceState.workflowState ==
                                        WorkflowState.PendingAccountOwnerApproval &&
                                        !termsChecked) ||
                                    submissionInProgress
                                }
                                onClick={() => {
                                    openDecisionModal('approve');
                                }}
                            />
                            <br />
                            <br />
                        </ButtonBar.Item>
                        <ButtonBar.Item separator>
                            <Button
                                name="Reject"
                                size="small"
                                isLoading={submissionInProgress}
                                disabled={submissionInProgress}
                                onClick={() => {
                                    openDecisionModal('reject');
                                }}
                            />
                            <br />
                            <br />
                        </ButtonBar.Item>
                    </>
                )}
                {account?.canHaveDMZDecision() && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Complete"
                            size="small"
                            isLoading={submissionInProgress}
                            disabled={submissionInProgress}
                            onClick={() => {
                                openDecisionModal('complete');
                            }}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}
                {account?.canHaveGroupApprovalDecision() && (
                    <>
                        <ButtonBar.Item separator>
                            <Button
                                name="Approve"
                                size="small"
                                isLoading={submissionInProgress}
                                disabled={submissionInProgress}
                                onClick={() => {
                                    openDecisionModal('approve');
                                }}
                            />
                            <br />
                            <br />
                        </ButtonBar.Item>
                        <ButtonBar.Item separator>
                            <Button
                                name="Reject"
                                size="small"
                                isLoading={submissionInProgress}
                                disabled={submissionInProgress}
                                onClick={() => {
                                    openDecisionModal('reject');
                                }}
                            />
                            <br />
                            <br />
                        </ButtonBar.Item>
                    </>
                )}
                {account?.isCancellable() && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Cancel"
                            size="small"
                            isLoading={submissionInProgress}
                            disabled={submissionInProgress}
                            onClick={() => {
                                openDecisionModal('cancel');
                            }}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}
                {account?.isCompleteAndDeletable() && featureFlags.enableAwsAccountDeletion && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Delete"
                            size="small"
                            isLoading={submissionInProgress}
                            disabled={submissionInProgress}
                            onClick={() => {
                                openDecisionModal('delete');
                            }}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}

                {account?.isAwaitingOwnerApprovalForDeletion() && featureFlags.enableAwsAccountDeletion && (
                   
                     <>
                        <ButtonBar.Item separator>
                        <Button
                            name="Approve Deletion"
                            size="small"
                            isLoading={submissionInProgress}
                            disabled={submissionInProgress}
                            onClick={() => {
                                openDecisionModal('delete');
                            }}
                        />
                        <br />
                        <br />
                        </ButtonBar.Item>
                        <ButtonBar.Item separator>
                            <Button
                                name="Reject Deletion"
                                size="small"
                                isLoading={submissionInProgress}
                                disabled={submissionInProgress}
                                onClick={() => {
                                    openDecisionModal('reject');
                                }}
                            />
                            <br />
                            <br />
                        </ButtonBar.Item>
                    </>
                )}
                {isPocAccount && account?.isExpiredAndRenewable() && featureFlags?.enableAwsPOCAccountRenewal && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Extend PoC"
                            size="small"
                            isLoading={submissionInProgress}
                            disabled={submissionInProgress}
                            onClick={() => setExtendPocModalVisible(true)}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}
                {account?.isExpiredAndDeletable() && featureFlags?.enableAwsAccountDeletion && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Delete"
                            size="small"
                            isLoading={submissionInProgress}
                            disabled={submissionInProgress}
                            onClick={() => openDecisionModal('delete')}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}
                {account?.isCompletedAndRenewable() && account?.isPocAccount() && featureFlags?.enableAwsPOCAccountRenewal && (
                    <ButtonBar.Item separator>
                        <Button
                            name="Extend PoC"
                            size="small"
                            isLoading={submissionInProgress}
                            disabled={submissionInProgress}
                            onClick={() => setExtendPocModalVisible(true)}
                        />
                        <br />
                        <br />
                    </ButtonBar.Item>
                )}
                {account?.canDecideOnRenewal() && featureFlags?.enableAwsPOCAccountRenewal && (
                    <>
                        <ButtonBar.Item separator>
                            <Button
                                name="Approve Extension"
                                size="small"
                                isLoading={submissionInProgress}
                                disabled={submissionInProgress}
                                onClick={() => setRenewalDecisionModal({ visible: true, decision: 'approve' })}
                            />
                            <br />
                            <br />
                        </ButtonBar.Item>
                        <ButtonBar.Item separator>
                            <Button
                                name="Delete Account"
                                size="small"
                                isLoading={submissionInProgress}
                                disabled={submissionInProgress}
                                onClick={() => setRenewalDecisionModal({ visible: true, decision: 'delete' })}
                            />
                            <br />
                            <br />
                        </ButtonBar.Item>
                    </>
                )}
            </ButtonBar>
        );
    };

    const handleCancel = () => {
        setEditNetworkConfiguration(false);
        handleStateFormChange('networkConfigList', []);
    };

    const handleSave = async (e) => {
        e?.preventDefault();
        setSubmissionInProgress(true);
        setSubmissionAlert({
            visible: true,
            text: 'Submitting request...',
            variant: 'default',
        });
        try {
            let model = {
                vpcs: state.stateForm.networkConfigList.map((net) => new RequestAWSAccountVPCModel(net)),
            };
            await awsAccountService.updateAccountRequestVpc(model, account.id);
            setSubmissionAlert({
                visible: true,
                text: 'Network Configuration saved. Approving request...',
                variant: 'success',
            });
            await awsAccountService.approveAccountRequest('Network Configuration uploaded', account.id);
            setSubmissionAlert({
                visible: true,
                text: 'Request approved successfully.',
                variant: 'success',
            });
            setTimeout(() => {
                navigate('/awsAccounts');
            }, 5000);
        } catch (err) {
            setSubmissionAlert({
                visible: true,
                variant: 'error',
                text: err.response?.data || 'An error occurred with your request. Please, try again later.',
            });
        }
        setSubmissionInProgress(false);
        setEditNetworkConfiguration(false);
    };

    const enterConnectivityEditMode = () => {
        setBaselineConnectivitySettings(
            account?.network ? account.network.map((net) => new AWSAccountConnectivityConfiguration({ ...net })) : []
        );
        setEditConnectivityMode(true);
    };

    const cancelConnectivityEdit = () => {
        if (baselineConnectivitySettings !== null) {
            handleStateFormChange('networkConfigList', baselineConnectivitySettings);
            fetchAccount();
        }
        setEditConnectivityMode(false);
        setBaselineConnectivitySettings(null);
    };

    const handleSaveConnectivitySettings = async (e) => {
        e?.preventDefault();
        setSubmissionInProgress(true);
        setSubmissionAlert({
            visible: true,
            text: 'Saving connectivity settings...',
            variant: 'default',
        });
        try {
            const model = {
                vpcs: state.stateForm.networkConfigList.map((net) => new RequestAWSAccountVPCModel(net)),
            };
            await awsAccountService.updateAccountRequestVpc(model, account.id);
            setSubmissionAlert({
                visible: true,
                text: 'Connectivity settings saved successfully.',
                variant: 'success',
            });
            setEditConnectivityMode(false);
            setBaselineConnectivitySettings(null);
            setTimeout(() => {
                navigate('/awsAccounts');
            }, 3000);
        } catch (err) {
            setSubmissionAlert({
                visible: true,
                variant: 'error',
                text: err.response?.data || 'An error occurred while saving connectivity settings. Please try again later.',
            });
        }
        setSubmissionInProgress(false);
    };

    const isExpired = account?.isExpired() ?? false;
    const isPocAccount = account?.isPocAccount() ?? false;

    const handleExtendPoc = async (pocDurationDays: number, reason: string) => {
        setExtendPocModalVisible(false);
        setSubmissionInProgress(true);
        setSubmissionAlert({ visible: true, text: 'Extending PoC...', variant: 'default' });
        try {
            await awsAccountService.extendPoc(account!.id, pocDurationDays, reason);
            setSubmissionAlert({ visible: true, text: 'PoC extension requested. It is now pending architect approval. Redirecting...', variant: 'success' });
            handleRedirect();
        } catch (err: any) {
            setSubmissionAlert({
                visible: true,
                text: err?.response?.data || 'Failed to extend PoC.',
                variant: 'error',
            });
        }
        setSubmissionInProgress(false);
    };

    return (
        <>
            <WorkflowDetailWrapper
                title={getFormattedTitle(account)}
                loading={loading}
                fromPOR
                workflowInstance={account?.workflowInstance}
                linkTree={[{ to: '/awsAccounts', text: 'AWS Accounts' }]}
                actions={Actions()}
            >
                {submissionAlert.visible && (
                    <Alert
                        variant={submissionAlert.variant}
                        body={submissionAlert.text}
                        hasButton
                        onClose={handleAlertClose}
                    />
                )}
                {isExpired && isPocAccount && (
                    <Alert
                        variant="error"
                        body={`This PoC account expired on ${account?.getPocExpirationDate()?.toLocaleDateString() ?? 'an unknown date'}. Extend the PoC or delete the account.`}
                        hasButton={false}
                    />
                )}
                {!isExpired && isPocAccount && (account?.getPocDaysRemaining() ?? 999) <= 14 && (
                    <Alert
                        variant="warning"
                        body={`This PoC account expires in ${account?.getPocDaysRemaining()} day(s) on ${account?.getPocExpirationDate()?.toLocaleDateString()}.`}
                        hasButton={false}
                    />
                )}
                {validationErrors.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                        {validationErrors.map((error, index) => (
                            <Alert
                                key={`${error.field}-${index}`}
                                variant="error"
                                body={`${error.field}: ${error.message}`}
                                hasButton={false}
                                style={{ marginBottom: index < validationErrors.length - 1 ? '10px' : '0' }}
                            />
                        ))}
                    </div>
                )}
                <OrganizationInput
                    organizationL3={stateFormHandler().selectedL3}
                    organizationL4={stateFormHandler().selectedL4}
                    globalOrganization={stateFormHandler().selectedOrganization}
                    disabled={!editMode}
                />
                <Grid variant="halves">
                    <Grid.Item>
                        <ToggleField
                            id="account-type"
                            label="Account Type"
                            {...stateFormHandler().isManaged}
                            disabled={true}
                            value={stateFormHandler().isManaged.value?.toString()}
                            options={[
                                { value: 'true', text: 'Managed AWS' },
                                { value: 'false', text: 'Disconnected AWS' },
                            ]}
                            note=""
                        />
                    </Grid.Item>
                    <Grid.Item>
                        <CheckboxGroup label="Account Subtypes">
                            <Checkbox
                                value="Tier0"
                                label="Tier 0"
                                id="tier0"
                                checked={isTier0}
                                disabled={!canEditTier0()}
                                onChange={handleTier0Change}
                            />
                            <Checkbox
                                value="CyberCritical"
                                label="CyberCritical"
                                id="cyberCritical"
                                checked={isCyberCritical}
                                disabled={!canEditCyberCritical()}
                                onChange={handleCyberCriticalChange}
                            />
                        </CheckboxGroup>
                    </Grid.Item>
                </Grid>
                <Grid>
                    <Grid.Item>
                        <TextInput
                            label="Internal Order"
                            {...stateFormHandler().internalOrder}
                            disabled={!editMode}
                            note={editMode ? 'Should start with G8020 and be followed by 5 digits' : ''}
                        />
                    </Grid.Item>
                    <Grid.Item>
                        <EnvironmentInput
                            isManaged={stateFormHandler().isManaged.value}
                            {...stateFormHandler().environment}
                            disabled={true}
                        />
                    </Grid.Item>
                </Grid>
                <TextInput
                    label="AWS APP Name"
                    value={stateFormHandler().appName.value}
                    onChange={stateFormHandler().appName.onChange}
                    disabled={!editMode}
                    validation={editMode ? stateFormHandler().appName.validation : undefined}
                    note="This field will be used to populate the account name, abbreviation can be used (Max 16 characters)"
                />
                <TextInput label="AWS Account Name" {...stateFormHandler().accountName} disabled={true} />

                {isPocAccount && (
                    <Grid variant="halves">
                        <Grid.Item>
                            <TextInput
                                label="PoC Duration"
                                value={`${account?.pocDurationDays} days`}
                                onChange={() => { }}
                                disabled={true}
                            />
                        </Grid.Item>
                        <Grid.Item>
                            <TextInput
                                label="PoC Expiration Date"
                                value={account?.getPocExpirationDate()?.toLocaleDateString() ?? 'Not started yet'}
                                onChange={() => { }}
                                disabled={true}
                            />
                        </Grid.Item>
                    </Grid>
                )}
                
                {renderRequiredGroupInputs()}

                <AccountGroupsEditor
                    isManaged={stateFormHandler().isManaged.value}
                    accountGroups={getAdditionalAccountGroups(
                        (state.stateForm as any).accountGroups || {},
                        requiredGroupAssignments
                    )}
                    onChange={handleAdditionalGroupsChange}
                    disabled={!canEditGroups()}
                    groupsService={groupsService}
                    permissionSets={permissionSets}
                    isCreationMode={false}
                />
                <br />
                <Grid variant="2up">
                    <Grid.Item>
                        <ToggleField
                            id="retained-app"
                            label="Is it a Retained App?"
                            {...stateFormHandler().isRetainedApp}
                            disabled={true}
                            value={stateFormHandler().isRetainedApp.value?.toString()}
                            options={[
                                { value: 'true', text: 'Yes' },
                                { value: 'false', text: 'No' },
                            ]}
                            note={''}
                        />
                    </Grid.Item>
                    {!stateFormHandler().isManaged.value ? (
                        <>
                            <Grid.Item>
                                <ToggleField
                                    id="ec2-instances"
                                    label="Requires EC2 Instances?"
                                    {...stateFormHandler().requiresEc2Instances}
                                    disabled={true}
                                    value={stateFormHandler().requiresEc2Instances.value?.toString()}
                                    options={[
                                        { value: 'true', text: 'Yes' },
                                        { value: 'false', text: 'No' },
                                    ]}
                                    note={''}
                                />
                            </Grid.Item>
                            <Grid.Item>
                                <ToggleField
                                    id="public-ips"
                                    label="Requires Public Ip Addresses?"
                                    {...stateFormHandler().requiresPublicIpAddresses}
                                    disabled={true}
                                    value={stateFormHandler().requiresPublicIpAddresses.value?.toString()}
                                    options={[
                                        { value: 'true', text: 'Yes' },
                                        { value: 'false', text: 'No' },
                                    ]}
                                    note={''}
                                />
                            </Grid.Item>
                            <Grid.Item>
                                <ToggleField
                                    id="container-services"
                                    label="Requires Native Container Services?"
                                    {...stateFormHandler().requiresNativeContainerServices}
                                    disabled={true}
                                    value={stateFormHandler().requiresNativeContainerServices.value?.toString()}
                                    options={[
                                        { value: 'true', text: 'Yes' },
                                        { value: 'false', text: 'No' },
                                    ]}
                                    note={''}
                                />
                            </Grid.Item>
                        </>
                    ) : (
                        <>
                            <Grid.Item>
                                <ToggleField
                                    id="network-isolation"
                                    label="Requires Network Isolation?"
                                    {...stateFormHandler().requiresIsolation}
                                    disabled={true}
                                    value={stateFormHandler().vpcSizeId.value ? 'true' : 'false'}
                                    options={[
                                        { value: 'true', text: 'Yes' },
                                        { value: 'false', text: 'No' },
                                    ]}
                                    note={''}
                                />
                            </Grid.Item>
                            <Grid.Item>
                                <ToggleField
                                    id="dmz"
                                    label="DMZ Required?"
                                    {...stateFormHandler().requiresDMZ}
                                    disabled={true}
                                    value={stateFormHandler().requiresDMZ.value?.toString()}
                                    options={[
                                        { value: 'true', text: 'Yes' },
                                        { value: 'false', text: 'No' },
                                    ]}
                                    note={''}
                                />
                            </Grid.Item>
                            {(stateFormHandler().requiresDMZ.value?.toString() === 'true' ||
                                stateFormHandler().requiresIsolation.value?.toString() === 'true') && (
                                    <Grid.Item>
                                        <MultipleSelectField
                                            label="Region"
                                            {...stateFormHandler().regionNames}
                                            options={[
                                                { value: 'us-east-2', text: 'Ohio (us-east-2)' },
                                                { value: 'eu-west-2', text: 'London (eu-west-2)' },
                                                { value: 'ap-southeast-1', text: 'Singapore (ap-southeast-1)' },
                                                { value: 'ap-south-1', text: 'Mumbai (ap-south-1)' },
                                                { value: 'ap-southeast-5', text: 'Malaysia (ap-southeast-5)' },
                                                { value: 'ap-southeast-4', text: 'Melbourne (ap-southeast-4)' },
                                                { value: 'sa-east-1', text: 'Sao Paulo (sa-east-1)' },
                                            ]}
                                            disabled={true}
                                            fullWidth
                                            note={''}
                                        />
                                    </Grid.Item>
                                )}
                            {stateFormHandler().requiresDMZ.value?.toString() === 'true' && (
                                <Grid.Item>
                                    <TextInput
                                        label="Number of Public Ips"
                                        {...stateFormHandler().numOfDmzPublicIpAddresses}
                                        disabled={true}
                                        note={''}
                                    />
                                </Grid.Item>
                            )}
                            {(stateFormHandler().requiresDMZ.value?.toString() === 'true' ||
                                stateFormHandler().requiresIsolation.value?.toString() === 'true') && (
                                    <>
                                        <Grid.Item>
                                            <Select
                                                label="Number of Servers"
                                                {...stateFormHandler().vpcSizeId}
                                                disabled={true}
                                                options={[
                                                    { value: '1', text: 'Small (1-10 servers)' },
                                                    { value: '2', text: 'Medium (11-50 servers)' },
                                                    { value: '3', text: 'Large (51-100 servers)' },
                                                ]}
                                            />
                                        </Grid.Item>
                                    </>
                                )}
                        </>
                    )}
                </Grid>
                <br id="connectivity-settings" />
                {account?.isManaged && (
                    <AccountConnectivitySettingsTable
                        {...stateFormHandler().networkConfig}
                        account={account}
                        readonly={!editNetConfiguration && !editConnectivityMode}
                    />
                )}
                <Grid variant="halves">
                    <Grid.Item>
                        <CartaInput {...stateFormHandler()?.cartaIds} disabled={!editMode} oneOptionOnly={true} />
                        {editMode && (
                            <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
                                Only 1 APP ID is admitted.
                            </p>
                        )}
                    </Grid.Item>
                </Grid>
                <Grid variant="halves">
                    <Grid.Item>
                        <UserLookup
                            label={'AWS Account Owner'}
                            {...stateFormHandler().owner}
                            disabled={true}
                            isAWSAccountOwner
                            isAWSAccount
                        />
                    </Grid.Item>
                    <Grid.Item>
                        <UserLookup
                            label={'AWS Account Custodian'}
                            {...stateFormHandler().custodian}
                            disabled={!editMode}
                            isAWSAccount
                        />
                    </Grid.Item>
                </Grid>
                <Textarea label="Business Justification" {...stateFormHandler().accountUsedFor} disabled={!editMode || !canEditBusinessJustification()} />
                {account?.workflowInstance.currentWorkflowInstanceState.workflowState.value ===
                    WorkflowState.PendingAccountOwnerApproval.value &&
                    account?.owner.id == authenticatedUser.userId && (
                        <>
                            <CheckboxGroup
                                label="Terms and conditions"
                                required
                                optionalClass={termsChecked ? 'em-is-valid' : 'em-has-error'}
                            >
                                <Checkbox
                                    value="1"
                                    label="Check here to confirm that account owner and account custodian meets the eligibility requirements - Amazon Web Services (AWS) Account Owner Responsibilities | AppWiki"
                                    id="check-terms"
                                    checked={termsChecked}
                                    onChange={(checked) => setTermsChecked(checked)}
                                />
                            </CheckboxGroup>
                            <>
                                For more info check this link:{' '}
                                <a
                                    href="https://appwiki.xom.cloud/docs/SpecializedCloud/AWS/account-owner.html"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    AWS Account Owner Responsibilities
                                </a>
                            </>
                        </>
                    )}
                {editNetConfiguration && (
                    <Grid variant="4up">
                        <Grid.Item>
                            <Button
                                variant="secondary"
                                onClick={handleCancel}
                                isLoading={loading || submissionInProgress}
                                loadingVariant="secondary"
                                disabled={loading || !editNetConfiguration || submissionInProgress}
                                isIconOnlyButton
                            >
                                <XFilledIcon />
                            </Button>
                        </Grid.Item>
                        <Grid.Item />
                        <Grid.Item />
                        <Grid.Item>
                            <Button
                                variant="primary"
                                disabled={
                                    stateFormHandler().networkConfig?.network?.length < 0 ||
                                    !editNetConfiguration ||
                                    (account?.workflowInstance.currentWorkflowInstanceState.workflowState ==
                                        WorkflowState.PendingAccountOwnerApproval &&
                                        !termsChecked) ||
                                    submissionInProgress
                                }
                                isLoading={loading || submissionInProgress}
                                onClick={(e) => handleSave(e)}
                                loadingVariant="secondary"
                                isIconOnlyButton
                            >
                                <SaveDiskIcon />
                            </Button>
                        </Grid.Item>
                    </Grid>
                )}
                {editConnectivityMode && (
                    <Grid variant="4up">
                        <Grid.Item>
                            <Button
                                variant="secondary"
                                onClick={cancelConnectivityEdit}
                                isLoading={loading || submissionInProgress}
                                loadingVariant="secondary"
                                disabled={loading || submissionInProgress}
                                isIconOnlyButton
                            >
                                <XFilledIcon />
                            </Button>
                        </Grid.Item>
                        <Grid.Item />
                        <Grid.Item />
                        <Grid.Item>
                            <Button
                                variant="primary"
                                disabled={
                                    state.stateForm.networkConfigList?.length === 0 ||
                                    submissionInProgress
                                }
                                isLoading={loading || submissionInProgress}
                                onClick={(e) => handleSaveConnectivitySettings(e)}
                                loadingVariant="secondary"
                                isIconOnlyButton
                            >
                                <SaveDiskIcon />
                            </Button>
                        </Grid.Item>
                    </Grid>
                )}
            </WorkflowDetailWrapper>
            <ConfirmModal
                visible={decisionModalVisible.approve}
                title={`Approve Account Request #${account?.id}`}
                question="Are you sure you want to approve this request?"
                confirmationField={decisionModalConfirmationField()}
                onConfirm={() => handleApprove()}
                onCancel={() => closeDecisionModal('approve')}
            />
            <ConfirmModal
                visible={decisionModalVisible.complete}
                title={`Complete Account Request #${account?.id}`}
                question="Are you sure you want to complete this request?"
                confirmationField={decisionModalConfirmationField()}
                onConfirm={() => handleComplete()}
                onCancel={() => closeDecisionModal('complete')}
            />
            <ConfirmModal
                visible={decisionModalVisible.reject}
                title={`Reject Account Request #${account?.id}`}
                question="Are you sure you want to reject this request?"
                confirmationField={decisionModalConfirmationField()}
                onConfirm={() => handleReject()}
                onCancel={() => closeDecisionModal('reject')}
            />
            <ConfirmModal
                visible={decisionModalVisible.cancel}
                title={`Cancel Account Request #${account?.id}`}
                question="Are you sure you want to cancel this request?"
                confirmationField={decisionModalConfirmationField()}
                onConfirm={() => handleCancellation()}
                onCancel={() => closeDecisionModal('cancel')}
            />
            <ConfirmModal
                visible={cancelTransferModalVisible}
                title={`Cancel Ownership Transfer #${account?.id}`}
                question="Are you sure you want to cancel the transfer?"
                confirmButton={{
                    label: 'Cancel Transfer',
                    props: { variant: 'primary', color: 'negative' },
                }}
                onConfirm={handleCancelTransferOwnership}
                onCancel={closeCancelTransferModal}
            />
            <ConfirmModal
                visible={decisionModalVisible.delete}
                title={`Delete Account Request #${account?.id}`}
                question="Are you sure you want to delete this request?"
                confirmationField={decisionModalConfirmationField()}
                onConfirm={() => handleDeletion()}
                onCancel={() => closeDecisionModal('delete')}
            />
            <TransferAwsAccountModal
                visible={transferModalVisible}
                ownerInTransfer={stateFormHandler().ownerInTransfer}
                newCustodian={stateFormHandler().custodianInTransfer}
                currentCustodian={account?.custodian ? UserLookupObject.fromUser(account.custodian) : null}
                onConfirm={handleTransferOwnership}
                onCancel={closeTransferModal}
            />
            <ExtendPocModal
                visible={extendPocModalVisible}
                accountName={account?.accountName ?? ''}
                accountId={account?.id}
                onConfirm={handleExtendPoc}
                onCancel={() => setExtendPocModalVisible(false)}
            />
            <RenewalDecisionModal
                visible={renewalDecisionModal.visible}
                decision={renewalDecisionModal.decision}
                accountName={account?.accountName ?? ''}
                accountId={account?.id}
                pocDurationDays={account?.pocDurationDays}
                currentExpirationDate={account?.getPocExpirationDate() ?? null}
                inProgress={submissionInProgress}
                onConfirm={
                    renewalDecisionModal.decision === 'approve'
                        ? handleApproveRenewal
                        : handleDeleteFromRenewal
                }
                onCancel={() => setRenewalDecisionModal({ ...renewalDecisionModal, visible: false })}
            />
        </>
    );
};

export default AWSAccountsDetail;
